// src/pages/company-admin/CompanyUsersPage.tsx
import React, { useEffect, useState } from 'react';
import {
    listCompanyUsers,
    updateCompanyMemberRole,
    deleteCompanyMember,
    type CompanyUser,
} from 'src/features/company/api';
import InviteCompanyUserModal from './InviteCompanyUserModal';
import { useToast } from 'src/shared/ui/toast/useToast';

type OrgUserRow = {
    id: string; // companyMemberId
    name: string;
    email: string;
    roleName: string; // COMPANY_ADMIN / COMPANY_USER ...
    status: string; // ACTIVE / INACTIVE ...
    joinedAt: string;
};

const CompanyUsersPage: React.FC = () => {
    const toast = useToast();

    const [rows, setRows] = useState<OrgUserRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const [roleSavingId, setRoleSavingId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const [inviteOpen, setInviteOpen] = useState(false);

    const mapToRow = (u: CompanyUser): OrgUserRow => ({
        id: u.id,
        name: u.user.name || '(이름 없음)',
        email: u.user.email,
        roleName: u.role.name,
        status: u.user.status ?? '',
        joinedAt: u.joinedAt,
    });

    const reload = async () => {
        setLoading(true);
        setErr(null);
        try {
            const data = await listCompanyUsers();
            setRows(data.map(mapToRow));
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? '회사 사용자 목록을 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void reload();
    }, []);

    const handleRoleChange = async (id: string, roleName: string) => {
        const target = rows.find((r) => r.id === id);

        setRoleSavingId(id);

        try {
            await updateCompanyMemberRole(id, roleName);
            setRows((prev) => prev.map((r) => (r.id === id ? { ...r, roleName } : r)));

            toast.success(
                `${target?.name || target?.email || '사용자'}의 역할이 ${roleName}로 변경되었습니다.`,
                { title: '역할 변경 완료' },
            );
        } catch (e: any) {
            toast.error(e?.response?.data?.error?.message ?? '역할 변경 중 오류가 발생했습니다.', {
                title: '역할 변경 실패'
            });
        } finally {
            setRoleSavingId(null);
        }
    };

    const handleDelete = async (id: string) => {
        const target = rows.find((r) => r.id !== id);

        if (!window.confirm('이 사용자를 이 회사에서 제외하시겠습니까? (계정 자체는 유지됩니다)')) {
            return;
        }

        setDeletingId(id);
        try {
            await deleteCompanyMember(id);
            setRows((prev) => prev.filter((r) => r.id !== id));

            toast.success(`${target?.name || target?.email || '사용자'}를 회사에서 제외했습니다.`, {
                title: '처리 완료',
            });
        } catch (e: any) {
            toast.error(e?.response?.data?.error?.message ?? '사용자 삭제 중 오류가 발생했습니다.', {
                title: '삭제 실패',
            });
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-sm font-semibold text-gray-900">Company Users</h2>
                    <p className="text-xs text-gray-500 mt-1">
                        이 회사에 소속된 사용자 목록입니다. 초대, 역할 변경, 제외를 할 수 있습니다.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setInviteOpen(true)}
                    className="px-3 py-1.5 rounded-full bg-sky-700 text-white text-xs hover:bg-sky-800"
                >
                    사용자 초대
                </button>
            </div>

            {err && (
                <div className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">
                    {err}
                </div>
            )}

            <div className="border-t border-gray-200" />

            <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                    <thead>
                        <tr className="bg-gray-50 text-xs text-gray-500">
                            <th className="px-3 py-2 font-medium">이름</th>
                            <th className="px-3 py-2 font-medium">이메일</th>
                            <th className="px-3 py-2 font-medium">역할</th>
                            <th className="px-3 py-2 font-medium">상태</th>
                            <th className="px-3 py-2 font-medium text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="text-xs">
                        {loading && (
                            <tr>
                                <td className="px-3 py-4 text-center text-gray-400" colSpan={5}>
                                    로딩 중…
                                </td>
                            </tr>
                        )}

                        {!loading && rows.length === 0 && (
                            <tr>
                                <td className="px-3 py-4 text-center text-gray-400" colSpan={5}>
                                    등록된 사용자가 없습니다. 오른쪽 상단의 &quot;사용자 초대&quot; 버튼을 눌러 사용자를
                                    추가하세요.
                                </td>
                            </tr>
                        )}

                        {!loading &&
                            rows.map((u) => (
                                <tr key={u.id} className="border-t">
                                    <td className="px-3 py-2">{u.name}</td>
                                    <td className="px-3 py-2">{u.email}</td>
                                    <td className="px-3 py-2">
                                        <select
                                            className="border rounded px-2 py-1 text-xs bg-white"
                                            value={u.roleName}
                                            onChange={(e) => handleRoleChange(u.id, e.target.value)}
                                            disabled={roleSavingId === u.id}
                                        >
                                            <option value="COMPANY_ADMIN">COMPANY_ADMIN</option>
                                            <option value="COMPANY_USER">COMPANY_USER</option>
                                        </select>
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        {u.status === 'ACTIVE' ? (
                                            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                                                ACTIVE
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                                                {u.status || 'UNKNOWN'}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right space-x-2">
                                        <button
                                            type="button"
                                            className="text-xs text-rose-600 hover:underline disabled:opacity-60"
                                            onClick={() => handleDelete(u.id)}
                                            disabled={deletingId === u.id}
                                        >
                                            회사에서 제외
                                        </button>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            <InviteCompanyUserModal
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
                onInvited={() => {
                    void reload();
                }}
            />
        </div>
    );
};

export default CompanyUsersPage;
